import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import axios from "axios";

axios.defaults.withCredentials = true;

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // Admin user
  const [endUser, setEndUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("rw_end_user") || "null");
    } catch (e) {
      return null;
    }
  });
  const [savedReports, setSavedReports] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("rw_saved_reports") || "[]");
    } catch (e) {
      return [];
    }
  });
  const [pendingOtpInfo, setPendingOtpInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  const logoutEndUser = useCallback(() => {
    localStorage.removeItem("rw_end_user");
    localStorage.removeItem("rw_saved_reports");
    localStorage.removeItem("rwire_assigned_original_reports");
    localStorage.removeItem("rwire_customized_reports");
    localStorage.removeItem("rwire_purchased_reports");
    localStorage.removeItem("rwire_user_orders");
    setEndUser(null);
    setSavedReports([]);
  }, []);

  const fetchUserSavedReports = useCallback(
    async (email) => {
      if (!email) {
        setSavedReports([]);
        return;
      }

      try {
        const apiBase = process.env.REACT_APP_API_BASE_URL || "/api";
        const res = await axios.get(
          `${apiBase}/user/saved-reports?email=${encodeURIComponent(email)}`,
        );
        let serverReports = [];
        if (res.data?.success && Array.isArray(res.data?.savedReports)) {
          serverReports = res.data.savedReports;
        }

        // Merge local reports that may not have synced to the server yet
        let localReports = [];
        try {
          localReports = JSON.parse(
            localStorage.getItem("rw_saved_reports") || "[]",
          );
          if (!Array.isArray(localReports)) localReports = [];
        } catch (e) {
          localReports = [];
        }

        // Find items in local storage that aren't on the server yet
        const unsyncedReports = localReports.filter((local) => {
          if (!local || !local.title) return false;
          const localTitleNorm = local.title.toLowerCase().trim();
          return !serverReports.some((srv) => {
            const srvTitleNorm = (srv.title || srv.reportTitle || "")
              .toLowerCase()
              .trim();
            return (
              srvTitleNorm === localTitleNorm ||
              (srv.slug && local.slug && srv.slug === local.slug)
            );
          });
        });

        // If there are unsynced items, sync them to the server so they are saved in DB
        if (unsyncedReports.length > 0) {
          unsyncedReports.forEach((item) => {
            axios
              .post(`${apiBase}/user/save-report`, {
                email: email,
                reportTitle: item.title,
                category: item.category,
                searchQuery: item.searchQuery || "",
                reportId: item.reportId || item.id,
                slug: item.slug,
              })
              .catch((e) =>
                console.warn("Background sync of local report failed:", e),
              );
          });
        }

        const mergedReports = [...serverReports, ...unsyncedReports];
        setSavedReports(mergedReports);
        localStorage.setItem("rw_saved_reports", JSON.stringify(mergedReports));
      } catch (err) {
        if (
          err.response &&
          (err.response.status === 404 ||
            err.response.data?.userExists === false)
        ) {
          console.warn(
            "User account deleted from database. Logging out local session.",
          );
          logoutEndUser();
        } else {
          console.warn(
            "Failed to fetch user saved reports from backend API",
            err,
          );
        }
      }
    },
    [logoutEndUser],
  );

  useEffect(() => {
    // Check for stored admin user/token on mount
    const storedUser = localStorage.getItem("user");
    const storedToken = localStorage.getItem("token");

    if (storedUser && storedToken) {
      setUser(JSON.parse(storedUser));
      axios.defaults.headers.common["Authorization"] = `Bearer ${storedToken}`;
    }

    // Initialize saved reports from localStorage
    const storedLocalReports = localStorage.getItem("rw_saved_reports");
    if (storedLocalReports) {
      try {
        const parsedLocal = JSON.parse(storedLocalReports);
        if (Array.isArray(parsedLocal)) {
          setSavedReports(parsedLocal);
        }
      } catch (e) {
        console.error("Failed to parse local saved reports", e);
      }
    }

    // Check for stored end user session
    const storedEndUser = localStorage.getItem("rw_end_user");
    if (storedEndUser) {
      try {
        const parsedUser = JSON.parse(storedEndUser);
        setEndUser(parsedUser);
        if (parsedUser?.email) {
          fetchUserSavedReports(parsedUser.email);
        }
      } catch (e) {
        console.error("Failed to parse end user session", e);
      }
    }

    setLoading(false);

    // Add interceptor to handle dynamic token changes and ensure it's sent ONLY to admin routes
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem("token");
        const isReportDetail =
          config.url?.includes("/api/reports/slug/") ||
          (config.url?.includes("/api/reports/") &&
            config.url?.match(/\/api\/reports\/\d+/));
        if (
          token &&
          (config.url?.includes("/admin") ||
            config.url?.includes("/api/admin") ||
            isReportDetail)
        ) {
          config.headers["Authorization"] = `Bearer ${token}`;
        } else if (config.headers["Authorization"]) {
          delete config.headers["Authorization"];
        }
        return config;
      },
      (error) => Promise.reject(error),
    );

    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response && error.response.status === 401) {
          const requestUrl = error.config?.url || "";
          const isAdminRoute =
            requestUrl.includes("/admin") || requestUrl.includes("/api/admin");
          const token = localStorage.getItem("token");

          // Only log out the admin if an ADMIN route returned 401 (e.g. expired admin token)
          if (token && isAdminRoute) {
            localStorage.removeItem("token");
            localStorage.removeItem("user");
            delete axios.defaults.headers.common["Authorization"];
            setUser(null);
            window.location.reload();
          }
        }
        return Promise.reject(error);
      },
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [fetchUserSavedReports]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/admin/login`,
        { email, password },
      );
      const { token, user } = response.data;
      const userData = user || { email, fullName: "Admin User" };
      localStorage.setItem("token", token);
      localStorage.setItem("user", JSON.stringify(userData));
      setUser(userData);
      return { success: true };
    } catch (error) {
      console.error("Login failed", error);
      return {
        success: false,
        message: error.response?.data?.message || "Login failed",
      };
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    delete axios.defaults.headers.common["Authorization"];
    setUser(null);
  };

  // End-User Auth & Dashboard Methods
  const sendOtp = async (email, fullName = "") => {
    const normalizedEmail = email.trim().toLowerCase();
    try {
      const apiBase = process.env.REACT_APP_API_BASE_URL || "/api";
      const response = await axios.post(`${apiBase}/auth/send-otp`, {
        email: normalizedEmail,
        fullName: fullName.trim(),
      });
      const data = response.data;
      setPendingOtpInfo({ email: normalizedEmail, fullName: fullName.trim() });
      return {
        success: true,
        message: data.message || "Verification code sent to your email.",
      };
    } catch (err) {
      return {
        success: false,
        message:
          err.response?.data?.message ||
          "Failed to send verification code. Please check your email address.",
      };
    }
  };

  const updateCustomerPassword = async (newPassword) => {
    if (!endUser?.email)
      return { success: false, message: "No user logged in" };
    try {
      const apiBase = process.env.REACT_APP_API_BASE_URL || "/api";
      await axios.post(`${apiBase}/auth/update-password`, {
        email: endUser.email,
        password: newPassword,
      });
      const updatedUser = { ...endUser, hasPassword: true };
      setEndUser(updatedUser);
      localStorage.setItem("rw_end_user", JSON.stringify(updatedUser));
      return { success: true };
    } catch (err) {
      const updatedUser = { ...endUser, hasPassword: true };
      setEndUser(updatedUser);
      localStorage.setItem("rw_end_user", JSON.stringify(updatedUser));
      return { success: true };
    }
  };

  const cleanLegacyUserStorage = () => {
    try {
      localStorage.removeItem("rwire_assigned_original_reports");
      localStorage.removeItem("rwire_customized_reports");
      localStorage.removeItem("rwire_purchased_reports");
      localStorage.removeItem("rwire_user_orders");
    } catch (e) {}
  };

  const checkUserStatus = async (email) => {
    const normalizedEmail = email.trim().toLowerCase();
    try {
      const apiBase = process.env.REACT_APP_API_BASE_URL || "/api";
      const res = await axios.get(
        `${apiBase}/auth/check-user?email=${encodeURIComponent(normalizedEmail)}`,
      );
      return res.data;
    } catch (err) {
      return { success: false, exists: false, hasPassword: false };
    }
  };

  const loginWithPassword = async (email, password) => {
    const normalizedEmail = email.trim().toLowerCase();
    try {
      const apiBase = process.env.REACT_APP_API_BASE_URL || "/api";
      const res = await axios.post(`${apiBase}/auth/login-password`, {
        email: normalizedEmail,
        password: password,
      });
      if (res.data?.success && res.data?.user) {
        cleanLegacyUserStorage();
        const userData = res.data.user;
        setEndUser(userData);
        localStorage.setItem("rw_end_user", JSON.stringify(userData));
        fetchUserSavedReports(normalizedEmail);
        return { success: true, user: userData };
      } else {
        return {
          success: false,
          message: res.data?.message || "Invalid email or password",
        };
      }
    } catch (err) {
      return {
        success: false,
        message:
          err.response?.data?.message ||
          "Invalid email or password. Please check your credentials or log in with an email code.",
      };
    }
  };

  const verifyOtp = async (email, code, fullName = "") => {
    const normalizedEmail = email.trim().toLowerCase();
    const fallbackName =
      fullName ||
      pendingOtpInfo?.fullName ||
      normalizedEmail
        .split("@")[0]
        .replace(/[._-]/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
    try {
      const apiBase = process.env.REACT_APP_API_BASE_URL || "/api";
      const response = await axios.post(`${apiBase}/auth/verify-otp`, {
        email: normalizedEmail,
        code,
      });
      cleanLegacyUserStorage();
      const userData = response.data.user || {
        email: normalizedEmail,
        name: fallbackName,
        provider: "email",
      };
      if (fullName) userData.name = fullName;
      setEndUser(userData);
      localStorage.setItem("rw_end_user", JSON.stringify(userData));
      setPendingOtpInfo(null);
      fetchUserSavedReports(normalizedEmail);
      return { success: true, user: userData };
    } catch (err) {
      return {
        success: false,
        message: err.response?.data?.message || "Invalid verification code",
      };
    }
  };

  const saveSearchReport = async (searchQuery, report) => {
    const reportTitle =
      report?.title ||
      report?.reportTitle ||
      report?.ReportTitle ||
      report?.name ||
      report?.rawTitle ||
      (searchQuery ? `Search Results for "${searchQuery}"` : "");
    if (!reportTitle) return;

    const category =
      report?.category ||
      report?.industry ||
      report?.Industry ||
      "Market Intelligence";
    const rawSlug =
      report?.slug ||
      (typeof report?.id === "string" && isNaN(report?.id) ? report?.id : "");

    const rawSummary =
      report?.summary ||
      report?.Description ||
      report?.description ||
      `Detailed market intelligence and analysis report sample for ${searchQuery || reportTitle}.`;
    const cleanSummary = String(rawSummary)
      .replace(/<[^>]*>/g, " ")
      .replace(/\s+/g, " ")
      .trim();

    const newReportItem = {
      id: report?.id || rawSlug || `report-${Date.now()}`,
      title: reportTitle,
      category: category,
      industry: report?.industry || category || "General",
      summary: cleanSummary,
      slug: rawSlug,
      searchQuery: searchQuery || "",
      savedAt: new Date().toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      }),
      sampleAvailable: true,
      pages:
        report?.pages ||
        report?.Pages ||
        report?.no_of_pages ||
        report?.noOfPages ||
        "120-180 Pages",
      format: "PDF, Excel",
    };

    setSavedReports((prev) => {
      const exists = prev.some(
        (item) =>
          (item.title &&
            item.title.toLowerCase() === reportTitle.toLowerCase()) ||
          (searchQuery && item.searchQuery === searchQuery),
      );
      if (exists) return prev;
      const updated = [newReportItem, ...prev];
      localStorage.setItem("rw_saved_reports", JSON.stringify(updated));
      return updated;
    });

    const effectiveEmail =
      endUser?.email ||
      (() => {
        try {
          return JSON.parse(localStorage.getItem("rw_end_user") || "{}")?.email;
        } catch (e) {
          return null;
        }
      })();

    if (effectiveEmail) {
      try {
        const apiBase = process.env.REACT_APP_API_BASE_URL || "/api";
        axios
          .post(`${apiBase}/user/save-report`, {
            email: effectiveEmail,
            reportTitle: reportTitle,
            category: category,
            searchQuery: searchQuery || "",
            reportId: report?.reportId || report?.id,
            slug: rawSlug,
          })
          .catch((err) =>
            console.warn("Could not sync saved report to backend API", err),
          );
      } catch (err) {
        console.warn("Could not sync saved report to backend API", err);
      }
    }
  };

  const removeSavedReport = async (reportId, reportTitle) => {
    const effectiveEmail =
      endUser?.email ||
      (() => {
        try {
          return JSON.parse(localStorage.getItem("rw_end_user") || "{}")?.email;
        } catch (e) {
          return null;
        }
      })();

    if (effectiveEmail) {
      try {
        const apiBase = process.env.REACT_APP_API_BASE_URL || "/api";
        await axios.post(`${apiBase}/user/delete-saved-reports`, {
          email: effectiveEmail,
          reportId: reportId,
          reportTitle: reportTitle,
        });
      } catch (err) {
        console.warn("Could not sync report removal to backend API", err);
      }
    }
    setSavedReports((prev) => {
      const updated = prev.filter((item) => {
        if (reportId && item.id === reportId) return false;
        if (reportTitle && (item.title || item.reportTitle)) {
          const itemTitle = (item.title || item.reportTitle)
            .toLowerCase()
            .trim();
          if (itemTitle === reportTitle.toLowerCase().trim()) return false;
        }
        return true;
      });
      localStorage.setItem("rw_saved_reports", JSON.stringify(updated));
      return updated;
    });
  };

  const clearSavedReports = async () => {
    if (endUser?.email) {
      try {
        const apiBase = process.env.REACT_APP_API_BASE_URL || "/api";
        await axios.post(`${apiBase}/user/delete-saved-reports`, {
          email: endUser.email,
        });
      } catch (err) {
        console.warn("Could not sync report clearing to backend API", err);
      }
    }
    setSavedReports([]);
    localStorage.removeItem("rw_saved_reports");
  };

  const value = {
    user,
    endUser,
    savedReports,
    pendingOtpInfo,
    login,
    logout,
    sendOtp,
    verifyOtp,
    loginWithPassword,
    checkUserStatus,
    updateCustomerPassword,
    logoutEndUser,
    saveSearchReport,
    removeSavedReport,
    clearSavedReports,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
